/**
 * ANALYTICS
 * - 総数（最大 1000 件を集計）
 * - 現像方針・フィルムの使用頻度（横棒、順に伸びる）
 * - シャドウ／ハイライトの配置傾向（ゾーンの灰色で塗った縦棒）
 * - 露出・コントラストの偏り（帯グラフ。40% を超えたら助言）
 * - 平均満足度
 */

import { showScreen, escapeHtml, barHtml, backBtn } from './ui.js';
import { aggregateStats } from './store.js';
import { getFilmById } from './data/films.js';
import { planLabel } from './zone-calc.js';

const ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

export async function openAnalytics() {
  const el = document.getElementById('screen-analytics');
  el.innerHTML = `${barHtml(backBtn())}<div class="page-head"><div><div class="eyebrow">analytics</div><div class="page-title">分析</div></div></div><div class="body" id="ana-body"></div>`;
  showScreen('analytics');
  let stats = null;
  try { stats = await aggregateStats(); } catch (e) { console.warn('[analytics] failed:', e); }
  const body = el.querySelector('#ana-body');
  if (!stats || stats.total === 0) {
    body.innerHTML = '<div class="empty"><div class="empty__t">データがまだありません</div><p class="empty__d">シーンを保存すると、統計が表示されます。</p></div>';
    return;
  }

  const advice = [];
  const exp = stats.exposureBias;
  const con = stats.contrastBias;
  if (exp?.underPct > 40) advice.push(`「アンダー」が ${exp.underPct.toFixed(0)}% を占めています。シャドウ配置を 1 段下げるか、EI を 1/3 段下げてみてください。`);
  if (exp?.overPct > 40) advice.push(`「オーバー」が ${exp.overPct.toFixed(0)}% を占めています。シャドウ配置を 1 段上げるか、EI を 1/3 段上げてみてください。`);
  const conAdvice = [];
  if (con?.contrastyPct > 40) conAdvice.push(`「硬調すぎ」が ${con.contrastyPct.toFixed(0)}% を占めています。現像の個人補正を −15〜30 秒試してください。`);
  if (con?.flatPct > 40) conAdvice.push(`「軟調すぎ」が ${con.flatPct.toFixed(0)}% を占めています。現像の個人補正を +15〜30 秒試してください。`);

  body.innerHTML = `
    <div style="padding:4px var(--gutter) 0;display:flex;align-items:baseline;gap:10px">
      <span style="font:300 72px/1 var(--font-en);letter-spacing:-.03em;animation:zn-roll .6s both">${stats.total}</span>
      <span class="sub" style="font-size:13px;line-height:1.5">シーン<br>最大 1000 件を集計</span>
    </div>
    ${section('現像方針の頻度', hbars(stats.planCount, (k) => planLabel(k), 48))}
    ${section('フィルムの使用頻度', hbars(stats.filmCount, (k) => getFilmById(k)?.name || k, 96))}
    ${section('シャドウの配置', vbars(stats.shadowZones), modeLabel(stats.shadowZones))}
    ${section('ハイライトの配置', vbars(stats.highlightZones), modeLabel(stats.highlightZones))}
    ${exp ? section('露出の偏り', band([['アンダー', exp.underPct, 'var(--color-ink)'], ['適正', exp.goodPct, 'var(--color-paper)'], ['オーバー', exp.overPct, 'var(--color-muted)']]) + adviceHtml(advice), `n = ${exp.n}`) : ''}
    ${con ? section('コントラストの偏り', band([['軟調すぎ', con.flatPct, 'var(--color-ink)'], ['計画どおり', con.asPlannedPct, 'var(--color-paper)'], ['硬調すぎ', con.contrastyPct, 'var(--color-muted)']]) + adviceHtml(conAdvice), `n = ${con.n}`) : ''}
    ${stats.avgSatisfaction != null ? section('平均満足度', `<div style="display:flex;align-items:baseline;gap:6px;margin-top:12px"><span style="font:300 48px/1 var(--font-en)">${stats.avgSatisfaction.toFixed(2)}</span><span class="en sub" style="font-size:16px">/ 5.0</span></div>`) : ''}
    <div style="height:40px"></div>
  `;
}

function section(title, inner, right = '') {
  return `<div style="margin:28px var(--gutter) 0;border-top:1px solid var(--color-ink);padding-top:12px">
    <div style="display:flex;justify-content:space-between"><span style="font:500 15px/1 var(--font-jp-sans)">${escapeHtml(title)}</span>${right ? `<span class="en sub" style="font-size:12px">${escapeHtml(right)}</span>` : ''}</div>
    ${inner}</div>`;
}

function hbars(map, labelFn, labelW) {
  const entries = Object.entries(map).sort(([, a], [, b]) => b - a);
  if (!entries.length) return '<p class="sub" style="font-size:12px">データなし</p>';
  const max = Math.max(...entries.map(([, v]) => v));
  return `<div class="hbars">${entries.map(([k, v], i) => `
    <div class="hbar" style="grid-template-columns:${labelW}px 1fr 28px"><span>${escapeHtml(labelFn(k))}</span><span class="hbar__track"><i style="width:${(v / max) * 100}%;animation-delay:${200 + i * 80}ms"></i></span><span>${v}</span></div>`).join('')}</div>`;
}

function vbars(map) {
  const vals = ROMAN.map((_, z) => map[z] || 0);
  const max = Math.max(1, ...vals);
  return `<div class="vbars">${vals.map((v, z) => `<span class="zc-${z}" style="height:${(v / max) * 100}%;animation-delay:${300 + z * 40}ms" title="${v}"></span>`).join('')}</div>
    <div class="zlabels" style="gap:3px;margin-top:6px;color:var(--color-muted-ink)">${ROMAN.map((r) => `<span>${r}</span>`).join('')}</div>`;
}

function modeLabel(map) {
  let best = null;
  Object.entries(map).forEach(([z, v]) => { if (!best || v > best[1]) best = [Number(z), v]; });
  return best ? `最多 ${ROMAN[best[0]]}` : '';
}

function band(parts) {
  return `<div class="band">${parts.map(([, v, bg], i) => `<span style="width:${v}%;background:${bg};animation-delay:${i * 120}ms"></span>`).join('')}</div>
    <div class="legend">${parts.map(([t, v, bg]) => `<span><i style="background:${bg}"></i>${t} <b>${v.toFixed(0)}%</b></span>`).join('')}</div>`;
}

function adviceHtml(list) {
  if (!list.length) return '';
  return `<div class="advice"><div class="eyebrow" style="margin-bottom:8px">advice</div>${list.map((a) => escapeHtml(a)).join('<br>')}</div>`;
}
